import { Card, IconTile } from "@design";
import { AppWindow, BookOpen, Bot, Link2, MessageCircle, Pin, Workflow, Zap } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { SpaceHubResponse, SpaceQuickActionSummary, SpaceResourceResponse } from "@/api";
import { SectionEmpty } from "@/components/section";
import { Text } from "@/components/Text";
import { spaceWorkflowPath } from "@/features/build/workflows/flowRunPaths";
import { knowledgePath } from "@/features/knowledge/KnowledgeTabs";
import { NoAccessNote, OnlyYouBadge } from "./ItemNotes";
import { SpaceSection } from "./SpaceSection";
import { spaceAppPath } from "./spaceApp";
import { newSpaceChatPath, withPrefill, withPrompt } from "./spaceChat";

const KIND_ICON: Record<string, ReactNode> = {
  agent: <Bot size={14} />,
  chat_session: <MessageCircle size={14} />,
  data_collection: <BookOpen size={14} />,
  link: <Link2 size={14} />,
  micro_app: <AppWindow size={14} />,
  quick_action: <Zap size={14} />,
  flow: <Workflow size={14} />,
};

function itemTitle(item: SpaceResourceResponse): string {
  return (
    item.title ??
    item.chatSessionTitle ??
    item.agentName ??
    item.dataCollectionName ??
    item.microAppName ??
    item.quickActionLabel ??
    item.flow?.name ??
    item.url ??
    ""
  );
}

function QuickActionCard({ action, onRun }: Readonly<{ action: SpaceQuickActionSummary; onRun?: () => void }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail.pinned" });
  return (
    <Card size="sm" onClick={onRun} className="w-full">
      <div className="flex items-start gap-2.5">
        <IconTile size="sm">
          <Zap size={14} />
        </IconTile>
        <div className="min-w-0 flex-1">
          <Text as="span" variant="body-sm" weight="semibold" className="block truncate">
            {action.label}
          </Text>
          {action.description && (
            <Text variant="caption" tone="tertiary" className="mt-1">
              {action.description}
            </Text>
          )}
          {action.canUse === false && (
            <Text variant="caption" tone="warning" className="mt-1">
              {t("qaNoAccess", { agent: action.agentName ?? "" })}
            </Text>
          )}
        </div>
      </div>
    </Card>
  );
}

function PinnedItemCard({ item, onOpen }: Readonly<{ item: SpaceResourceResponse; onOpen?: () => void }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail.pinned" });
  return (
    <Card size="sm" onClick={onOpen} className="flex w-full flex-col">
      <div className="mb-2 flex items-center gap-2">
        <IconTile size="sm">{KIND_ICON[item.resourceType] ?? <Pin size={14} />}</IconTile>
        <Text as="span" variant="eyebrow" tone="tertiary">
          {t(`kind.${item.resourceType}` as "kind.link", { defaultValue: item.resourceType })}
        </Text>
      </div>
      <span className="flex items-center gap-2">
        <Text as="span" variant="body-sm" weight="semibold" className="truncate">
          {itemTitle(item)}
        </Text>
        <OnlyYouBadge item={item} />
      </span>
      <NoAccessNote item={item} />
    </Card>
  );
}

/** Quick actions (start a chat in the space) and pinned items. Every member can start chats here. */
export function PinnedSection({ space }: Readonly<{ space: SpaceHubResponse }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail.pinned" });
  const navigate = useNavigate();
  const actions = (space.quickActions ?? []).filter((a) => a.isEnabled !== false);
  const pinned = space.pinned ?? [];
  const canStart = true; // any member (viewer+) can chat in the space

  const runAction = (a: SpaceQuickActionSummary) =>
    navigate(newSpaceChatPath(space.uuid, a.agentUuid), {
      state: a.autoSubmit ? withPrompt(a.promptTemplate) : withPrefill(a.promptTemplate),
    });

  const openItem = (item: SpaceResourceResponse): (() => void) | undefined => {
    if (item.canUse === false) return undefined;
    if (item.resourceType === "chat_session" && item.chatSessionUuid)
      return () => navigate(`/chat/${item.chatSessionUuid}`);
    if (item.resourceType === "link" && item.url) return () => window.open(item.url ?? "", "_blank", "noopener");
    if (item.resourceType === "agent" && item.agentUuid && canStart)
      return () => navigate(newSpaceChatPath(space.uuid, item.agentUuid));
    const flowUuid = item.flow?.uuid;
    if (item.resourceType === "flow" && flowUuid) return () => navigate(spaceWorkflowPath(space.uuid, flowUuid));
    const appUuid = item.microAppUuid;
    if (item.resourceType === "micro_app" && appUuid) return () => navigate(spaceAppPath(space.uuid, appUuid));
    if (item.resourceType === "data_collection")
      return () => {
        navigate(knowledgePath(space.uuid, "files"));
      };
    return undefined;
  };

  return (
    <SpaceSection title={t("title")} hint={t(space.isPersonal ? "privateHint" : "hint")}>
      {actions.length === 0 && pinned.length === 0 ? (
        <SectionEmpty icon={<Pin size={18} />} hint={t("empty")} />
      ) : (
        <div className="flex flex-col gap-2.5">
          {actions.length > 0 && (
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {actions.map((a) => (
                <QuickActionCard
                  key={a.uuid}
                  action={a}
                  onRun={canStart && a.canUse !== false ? () => runAction(a) : undefined}
                />
              ))}
            </div>
          )}
          {pinned.length > 0 && (
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {pinned.map((item) => (
                <PinnedItemCard key={item.uuid} item={item} onOpen={openItem(item)} />
              ))}
            </div>
          )}
        </div>
      )}
    </SpaceSection>
  );
}
